import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface ImageViewerProps {
  src: string | null;
  alt?: string;
  onClose: () => void;
}

/**
 * ImageViewer — full-screen lightbox dialog for viewing images at full size.
 * Closes on backdrop click, Escape key, or X button.
 */
export function ImageViewer({ src, alt = '', onClose }: ImageViewerProps) {
  // Close on Escape key
  useEffect(() => {
    if (!src) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [src, onClose]);

  if (!src) return null;

  const safeSrc = sanitizeUrl(src);
  if (!safeSrc) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-fade-in"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors cursor-pointer z-10"
        aria-label="Close image viewer"
      >
        <X className="size-6" />
      </button>

      {/* Image */}
      <img
        src={safeSrc}
        alt={alt || 'Expanded view'}
        className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl select-none"
        onClick={(e) => e.stopPropagation()}
        onError={(e) => {
          // Hide viewer if image fails to load
          (e.target as HTMLImageElement).style.display = 'none';
          onClose();
        }}
      />
    </div>
  );
}

export default ImageViewer;